import React, { useEffect, useState } from 'react';
import { FluentProvider, webDarkTheme, webLightTheme } from '@fluentui/react-components';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Dashboard from '../pages/dashboard/Dashboard';
import Header from '../components/header/Header';
import Navigation from '../components/navigation/Navigation';
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { useAppStyles } from './App.styles';
import Latency from '../pages/latency/latency';
import Reports from '../pages/reports/reports';
import ChatPage from '../pages/chat/ChatPage';
import { BatchDashboard } from '../pages/batch-dashboard';
import { AuthenticationResult, InteractionType } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode'
import Configuration from "../config/einvoicing-configuration";

function AuthLoading() {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p>Signing in...</p>
    </div>;
}

function AuthError({ error }: { error: any }) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p>Sign-in error: {error?.errorCode || 'unknown'} — {error?.message || 'Unknown error'}</p>
        <button onClick={() => { sessionStorage.clear(); window.location.href = '/'; }}>Clear state and retry</button>
    </div>;
}


function App() {

    const [darkTheme, setDarkTheme] = React.useState<boolean>(false);

    const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
    const { instance, accounts } = useMsal();
    const [accessToken, setAccessToken] = useState('');

    // Fix FluentUI Tabster dummy elements that are focusable while aria-hidden
    useEffect(() => {
        const fixTabsterDummies = () => {
            document.querySelectorAll<HTMLElement>('[data-tabster-dummy][aria-hidden="true"][tabindex="0"]').forEach(el => {
                el.setAttribute('tabindex', '-1');
            });
        };
        const observer = new MutationObserver(fixTabsterDummies);
        observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['tabindex', 'aria-hidden'] });
        fixTabsterDummies();
        return () => observer.disconnect();
    }, []);

    async function getAccessToken() {
        if (accessToken === '') {
            const request = {
                ...Configuration.msalTokenRequest,
                account: accounts[0]
            };
            const result: AuthenticationResult = await instance.acquireTokenSilent(request);
            setAccessToken(result.idToken);
            return result.idToken
        }
        return accessToken;
    }

    function useAdminCheck() {
        useEffect(() => {
            async function startLatency() {
                if (!ignore) {
                    try {
                        const token = await getAccessToken();
                        const decodedToken: any = jwtDecode(token);
                        const roles: string[] = decodedToken['roles'];
                        if (roles.includes('EInvoicingDashboard.Admin')) {
                            setIsAdmin(true);
                        }
                        else {
                            setIsAdmin(false);
                        }
                    }
                    catch {
                    }
                }
            }

            let ignore = false;
            startLatency();
            return () => {
                ignore = true;
            }
        }, []);
    }

    useAdminCheck();

    const appStyles = useAppStyles();

    return (
        <FluentProvider theme={darkTheme ? webDarkTheme : webLightTheme}>
            <MsalAuthenticationTemplate
                interactionType={InteractionType.Redirect}
                authenticationRequest={Configuration.msalTokenRequest}
                loadingComponent={AuthLoading}
                errorComponent={AuthError}
            >
                <div className={appStyles.primaryContainer} dir="ltr" lang="en">
                    <a href="#main" className="skip-nav-link" onClick={(e) => { e.preventDefault(); const main = document.getElementById('main'); if (main) { main.focus(); main.scrollIntoView(); } }}>Skip to main content</a>
                    <Header setDarkTheme={setDarkTheme} darkTheme={darkTheme} />
                    <BrowserRouter>
                        <div style={{ display: 'flex', flexGrow: 1, minHeight: 'calc(100vh - 3rem)' }}>
                            <Navigation isAdmin={isAdmin}/>
                            <main id='main' aria-label="Primary content" tabIndex={-1} className={appStyles.root} style={{ overflow: 'auto', width: '100%', marginLeft: 0 }}>
                                <Routes>
                                    <Route path="/" element={<Dashboard isAdmin={isAdmin} />} />
                                    <Route path="/batches" element={<BatchDashboard />} />
                                    <Route path="/latency" element={<Latency />} />
                                    {isAdmin && <Route path="/reports" element={<Reports />} />}
                                    {isAdmin && <Route path="/chat" element={<ChatPage />} />}
                                </Routes>
                            </main>
                        </div>
                    </BrowserRouter>
                </div>
            </MsalAuthenticationTemplate>
        </FluentProvider>
    );
}

export default App;
